import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import {
  X, CheckCircle2, AlertTriangle, XCircle, RefreshCw, Clock,
  Loader2, FileCheck2, SkipForward, History,
} from 'lucide-react';
import { SourceCheck, SourceIngestion } from '../../../api/apiTypes';
import { fetchSourceChecks, fetchSourceIngestions } from '../../../api/functionApi';

interface Props {
  sourceId: string;
  /** Nome file o URI, per intestare il pannello. */
  sourceLabel: string;
  onClose: () => void;
}

/** Come si presenta una riga: etichetta in italiano, icona e tinta dello stato. */
interface Aspetto {
  label: string;
  Icon: React.ComponentType<{ className?: string }>;
  /** Sfondo del riquadro dell'icona. */
  bolla: string;
  /** Colore dell'icona. */
  icona: string;
  /** Bordo e sfondo della riga. */
  riga: string;
}

const NEUTRO = 'border-borderDivider bg-white';
const OK = 'border-green-200 bg-green-50/50';
const ATTENZIONE = 'border-amber-200 bg-amber-50/50';
const ERRORE = 'border-red-200 bg-red-50/50';

const ESITO_CHECK: Record<SourceCheck['outcome'], Aspetto> = {
  started: {
    label: 'Controllo avviato', Icon: Loader2,
    bolla: 'bg-gray-50', icona: 'text-textPrimary/50', riga: NEUTRO,
  },
  unchanged: {
    label: 'Invariata', Icon: CheckCircle2,
    bolla: 'bg-green-100', icona: 'text-green-600', riga: OK,
  },
  changed: {
    label: 'Cambiata all’origine', Icon: AlertTriangle,
    bolla: 'bg-amber-100', icona: 'text-amber-600', riga: ATTENZIONE,
  },
  unreachable: {
    label: 'Origine non raggiungibile', Icon: XCircle,
    bolla: 'bg-red-100', icona: 'text-red-600', riga: ERRORE,
  },
  acknowledged: {
    label: 'Versione attuale confermata', Icon: CheckCircle2,
    bolla: 'bg-green-100', icona: 'text-green-600', riga: OK,
  },
  switched: {
    label: 'Sostituita con una nuova versione', Icon: RefreshCw,
    bolla: 'bg-violet-50', icona: 'text-violet-500', riga: NEUTRO,
  },
};

const STATO_INGESTION: Record<SourceIngestion['status'], Aspetto> = {
  pending: {
    label: 'In attesa', Icon: Clock,
    bolla: 'bg-gray-50', icona: 'text-textPrimary/50', riga: NEUTRO,
  },
  running: {
    label: 'In corso', Icon: Loader2,
    bolla: 'bg-blue-50', icona: 'text-blue-500', riga: NEUTRO,
  },
  success: {
    label: 'Riuscita', Icon: FileCheck2,
    bolla: 'bg-green-100', icona: 'text-green-600', riga: OK,
  },
  error: {
    label: 'Fallita', Icon: XCircle,
    bolla: 'bg-red-100', icona: 'text-red-600', riga: ERRORE,
  },
  skipped: {
    label: 'Saltata — risultato identico già presente', Icon: SkipForward,
    bolla: 'bg-gray-50', icona: 'text-textPrimary/50', riga: NEUTRO,
  },
};

const FALLBACK: Aspetto = {
  label: '', Icon: AlertTriangle,
  bolla: 'bg-gray-50', icona: 'text-textPrimary/50', riga: NEUTRO,
};

/**
 * Storico di una fonte: controlli di freschezza e indicizzazioni, dal più recente.
 * Le indicizzazioni sono una per tipo documento a cui la fonte è collegata.
 *
 * Modale piccola e centrata: il contenuto è una lista breve e non giustifica un
 * pannello a tutta altezza sul lato destro.
 */
const SourceHistoryModal: React.FC<Props> = ({ sourceId, sourceLabel, onClose }) => {
  const [checks, setChecks] = useState<SourceCheck[]>([]);
  const [ingestions, setIngestions] = useState<SourceIngestion[]>([]);
  const [loading, setLoading] = useState(true);
  const [errore, setErrore] = useState('');

  useEffect(() => {
    let annullato = false;
    setLoading(true);
    setErrore('');

    Promise.all([fetchSourceChecks(sourceId, 50), fetchSourceIngestions(sourceId, 50)])
      .then(([c, i]) => {
        if (annullato) return;
        setChecks(c);
        setIngestions(i);
      })
      .catch((err) => {
        console.error('Storico fonte non caricabile:', err);
        if (!annullato) setErrore(err instanceof Error ? err.message : 'Storico non disponibile.');
      })
      .finally(() => {
        if (!annullato) setLoading(false);
      });

    return () => {
      annullato = true;
    };
  }, [sourceId]);

  const data = (v?: string | null) => (v ? new Date(v).toLocaleString('it-IT') : '–');

  /** Riga nello stile delle card di Conduct Risk: bolla con icona, testo, data. */
  const Riga: React.FC<{
    aspetto: Aspetto;
    quando: string;
    dettaglio?: string | null;
    errore?: string | null;
    index: number;
  }> = ({ aspetto, quando, dettaglio, errore: err, index }) => {
    const { Icon } = aspetto;
    return (
      <motion.li
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: index * 0.03 }}
        className={`flex items-start gap-3 p-2.5 rounded-xl border ${aspetto.riga}`}
      >
        <div className={`w-7 h-7 rounded-lg flex items-center justify-center shrink-0 ${aspetto.bolla}`}>
          <Icon className={`w-3.5 h-3.5 ${aspetto.icona}`} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline justify-between gap-2">
            <span className="text-xs font-semibold text-textPrimary">{aspetto.label}</span>
            <span className="text-[11px] text-textMuted shrink-0">{quando}</span>
          </div>
          {dettaglio && <div className="text-[11px] text-textMuted mt-0.5 break-all">{dettaglio}</div>}
          {err && <div className="text-[11px] text-red-700 mt-0.5 break-words">{err}</div>}
        </div>
      </motion.li>
    );
  };

  const Sezione: React.FC<{ titolo: string; conteggio: number; children: React.ReactNode }> = ({
    titolo, conteggio, children,
  }) => (
    <section>
      <div className="flex items-center gap-2 mb-2">
        <h4 className="text-xs font-semibold text-textSecondary">{titolo}</h4>
        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-gray-50 text-textSecondary border border-borderDivider">
          {conteggio}
        </span>
      </div>
      {children}
    </section>
  );

  return (
    <div className="fixed inset-0 bg-modalOverlay flex items-center justify-center z-[70] p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.97, y: 8 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ duration: 0.15 }}
        className="bg-surface rounded-2xl shadow-xl w-full max-w-md max-h-[75vh] overflow-y-auto p-5 flex flex-col gap-4"
      >
        {/* Intestazione */}
        <div className="flex justify-between items-start gap-3">
          <div className="flex items-center gap-2.5 min-w-0">
            <div className="w-8 h-8 rounded-lg bg-gray-50 flex items-center justify-center shrink-0">
              <History className="w-4 h-4 text-textPrimary/50" />
            </div>
            <div className="min-w-0">
              <h3 className="text-sm font-semibold text-textPrimary">Storico fonte</h3>
              <p className="text-[11px] text-textMuted break-all mt-0.5">{sourceLabel}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="shrink-0 text-textMuted hover:text-textPrimary transition-colors"
            aria-label="Chiudi"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {loading && (
          <div className="flex items-center gap-2 text-xs text-textMuted py-4 justify-center">
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
            Caricamento…
          </div>
        )}

        {errore && (
          <div className="flex items-start gap-2 p-2.5 rounded-lg border border-red-200 bg-red-50 text-[11px] text-red-800">
            <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-px" />
            <span className="break-words">{errore}</span>
          </div>
        )}

        {!loading && !errore && (
          <>
            <Sezione titolo="Controlli di freschezza" conteggio={checks.length}>
              {checks.length === 0 ? (
                <p className="text-[11px] text-textMuted">Nessun controllo registrato.</p>
              ) : (
                <ul className="flex flex-col gap-2">
                  {checks.map((c, idx) => (
                    <Riga
                      key={c.id}
                      index={idx}
                      aspetto={ESITO_CHECK[c.outcome] ?? { ...FALLBACK, label: c.outcome }}
                      quando={data(c.checked_at)}
                      dettaglio={
                        c.new_source_catalog_id ? `Nuova versione: ${c.new_source_catalog_id}` : null
                      }
                      errore={c.error}
                    />
                  ))}
                </ul>
              )}
            </Sezione>

            <Sezione titolo="Indicizzazioni" conteggio={ingestions.length}>
              {ingestions.length === 0 ? (
                <p className="text-[11px] text-textMuted">
                  Nessuna indicizzazione: la classe risulterà da indicizzare.
                </p>
              ) : (
                <ul className="flex flex-col gap-2">
                  {ingestions.map((i, idx) => (
                    <Riga
                      key={i.id}
                      index={idx}
                      aspetto={STATO_INGESTION[i.status] ?? { ...FALLBACK, label: i.status }}
                      quando={data(i.completed_at ?? i.started_at ?? i.created_at)}
                      dettaglio={i.document_type}
                      errore={i.error_message}
                    />
                  ))}
                </ul>
              )}
            </Sezione>
          </>
        )}
      </motion.div>
    </div>
  );
};

export default SourceHistoryModal;
